import React, { useState } from "react";
import { Database, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/primitives/Button";
import { Dialog } from "@/components/primitives/Dialog";
import { Header } from "@/components/layout/Header";
import { PageShell } from "@/components/layout/PageShell";
import { DbConnectionForm } from "@/components/features/DbConnectionForm";
import { useDbConnections } from "@/hooks/useDbConnections";
import type { DbConnection, DbConnectionCreate } from "@/types";

export const DbConnectionsPage: React.FC = () => {
  const {
    connections,
    isLoading,
    error,
    refresh,
    create,
    update,
    remove,
    test,
    deploy,
    resetToken,
    uninstall,
  } = useDbConnections();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<DbConnection | null>(null);
  const [testMessage, setTestMessage] = useState<Record<string, string>>({});
  const [deployingIds, setDeployingIds] = useState<Set<string>>(new Set());
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [uninstallingId, setUninstallingId] = useState<string | null>(null);

  const handleNew = () => {
    setEditingConnection(null);
    setIsModalOpen(true);
  };

  const handleEdit = (connection: DbConnection) => {
    setEditingConnection(connection);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingConnection(null);
  };

  const handleSubmit = async (payload: DbConnectionCreate): Promise<boolean> => {
    let success: boolean;
    if (editingConnection) {
      const result = await update(editingConnection.id, payload);
      success = result !== null;
    } else {
      const result = await create(payload);
      success = result !== null;
    }
    if (success) {
      handleClose();
      await refresh();
    }
    return success;
  };

  const handleDelete = async (connection: DbConnection) => {
    if (window.confirm(`Delete connection "${connection.name}"?`)) {
      await remove(connection.id);
    }
  };

  const handleTest = async (connection: DbConnection) => {
    setTestMessage((prev) => ({ ...prev, [connection.id]: "Testing…" }));
    const result = await test(connection.id);
    setTestMessage((prev) => ({
      ...prev,
      [connection.id]: result ? result.message : "Test failed",
    }));
  };

  const handleDeploy = async (connection: DbConnection): Promise<boolean> => {
    if (deployingIds.has(connection.id)) {
      return false;
    }
    setDeployingIds((prev) => new Set(prev).add(connection.id));
    const result = await deploy(connection.id);
    setDeployingIds((prev) => {
      const next = new Set(prev);
      next.delete(connection.id);
      return next;
    });
    if (result) {
      setTestMessage((prev) => ({
        ...prev,
        [connection.id]: `Agent deployed on ${result.agent_url}`,
      }));
      return true;
    }
    return false;
  };

  const handleResetToken = async (connection: DbConnection): Promise<boolean> => {
    setResettingId(connection.id);
    const result = await resetToken(connection.id);
    setResettingId(null);
    if (result) {
      setTestMessage((prev) => ({
        ...prev,
        [connection.id]: result.message,
      }));
      return true;
    }
    return false;
  };

  const handleUninstall = async (connection: DbConnection): Promise<boolean> => {
    if (!window.confirm(`Uninstall the remote agent for "${connection.name}"? This stops the service and removes files from the remote server.`)) {
      return false;
    }
    setUninstallingId(connection.id);
    const result = await uninstall(connection.id);
    setUninstallingId(null);
    if (result) {
      setTestMessage((prev) => ({
        ...prev,
        [connection.id]: result.message,
      }));
      return true;
    }
    return false;
  };

  const formatHeartbeat = (connection: DbConnection): string => {
    if (connection.db_type !== "sqlite" || connection.connection.mode !== "agent") {
      return "";
    }
    if (!connection.last_heartbeat_at) {
      return "No heartbeat yet";
    }
    const date = new Date(connection.last_heartbeat_at);
    return `Heartbeat: ${date.toLocaleString()}`;
  };

  return (
    <PageShell>
      <Header
        action={
          <Button onClick={handleNew} className="gap-1.5">
            <Plus className="h-4 w-4" />
            New connection
          </Button>
        }
      />

      <main className="page-container py-10">
        <div className="mb-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Database connections
          </h2>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Manage reusable connection profiles for database health-check steps.
          </p>
        </div>

        {isLoading && (
          <div className="py-20 text-center text-slate-500 dark:text-slate-400">
            Loading connections…
          </div>
        )}
        {error && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        {!isLoading && connections.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 py-20 text-center dark:border-slate-700 dark:bg-slate-900/30">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-softnu-50 text-softnu-600 dark:bg-softnu-900/30 dark:text-softnu-300">
              <Database className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">
              No connections yet
            </h3>
            <p className="mb-6 max-w-sm text-slate-500 dark:text-slate-400">
              Create a connection profile so flows can query databases.
            </p>
            <Button onClick={handleNew}>Create a connection</Button>
          </div>
        )}

        {!isLoading && connections.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                  <tr>
                    <th className="px-6 py-4 font-medium">Name</th>
                    <th className="px-6 py-4 font-medium">Type</th>
                    <th className="px-6 py-4 font-medium">Host / Path</th>
                    <th className="px-6 py-4 font-medium">Database</th>
                    <th className="px-6 py-4 font-medium">SSL / Heartbeat</th>
                    <th className="px-6 py-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {connections.map((connection) => (
                    <tr
                      key={connection.id}
                      className="transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
                    >
                      <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                        {connection.name}
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                        {connection.db_type}
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                        {(connection.connection.host as string) ||
                          (connection.connection.agent_url as string) ||
                          (connection.connection.database as string) ||
                          "—"}
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                        {(connection.connection.database as string) ||
                          (connection.connection.db_path as string) ||
                          "—"}
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                        {connection.db_type === "sqlite" && connection.connection.mode === "agent"
                          ? formatHeartbeat(connection) || "Agent"
                          : connection.connection.ssl
                            ? "Yes"
                            : "No"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-end gap-2 sm:flex-row sm:justify-end">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => handleTest(connection)}
                            disabled={
                              connection.db_type === "sqlite" &&
                              connection.connection.mode === "agent" &&
                              (!connection.connection.agent_url || !connection.connection.agent_token)
                            }
                            title={
                              connection.db_type === "sqlite" &&
                              connection.connection.mode === "agent" &&
                              (!connection.connection.agent_url || !connection.connection.agent_token)
                                ? "Deploy the agent before testing"
                                : undefined
                            }
                          >
                            Test
                          </Button>
                          {connection.db_type === "sqlite" && connection.connection.mode === "agent" && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => handleDeploy(connection)}
                              isLoading={deployingIds.has(connection.id)}
                            >
                              Deploy
                            </Button>
                          )}
                          {connection.db_type === "sqlite" &&
                            connection.connection.mode === "agent" &&
                            Boolean(connection.connection.agent_url) && (
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                onClick={() => handleUninstall(connection)}
                                isLoading={uninstallingId === connection.id}
                              >
                                Uninstall
                              </Button>
                            )}
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleEdit(connection)} className="gap-1">
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(connection)}
                            className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </Button>
                        </div>
                        {testMessage[connection.id] && (
                          <p className="mt-2 text-right text-xs text-slate-500 dark:text-slate-400">
                            {testMessage[connection.id]}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <Dialog
        isOpen={isModalOpen}
        onClose={handleClose}
        title={editingConnection ? "Edit connection" : "New connection"}
      >
        <DbConnectionForm
          connection={editingConnection}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          onDeploy={editingConnection ? () => handleDeploy(editingConnection) : undefined}
          onResetToken={editingConnection ? () => handleResetToken(editingConnection) : undefined}
          onUninstall={editingConnection ? () => handleUninstall(editingConnection) : undefined}
          isDeploying={editingConnection ? deployingIds.has(editingConnection.id) : false}
          isResettingToken={editingConnection ? resettingId === editingConnection.id : false}
          isUninstalling={editingConnection ? uninstallingId === editingConnection.id : false}
        />
      </Dialog>
    </PageShell>
  );
};
